import { DateTime } from 'luxon';
import * as R from 'ramda';

import { SpecialHoursEntryAttrs, SpecialHoursInputEntry, DatePeriod } from './types';
import { WeeklyScheduleEvent, DayOfWeek, WeeklySchedule } from '../types';
import { DAYS_OF_WEEK } from '../values';

const SECOND_DURATION_MS = 1000;
const MINUTE_DURATION_MS = 60 * SECOND_DURATION_MS;
const HOUR_DURATION_MS = 60 * MINUTE_DURATION_MS;

export const extractTimePairs = (
  closedAllDay: boolean,
  open24Hours: boolean,
  timePairs: { startMinutes: number; endMinutes: number }[],
): { startMinutes: number; endMinutes: number }[] => {
  if (closedAllDay) {
    return [];
  }

  if (open24Hours) {
    return [
      {
        startMinutes: 0,
        endMinutes: 24 * 60,
      },
    ];
  }

  return timePairs;
};

const getExceptionOpeningTimes = (timePairs: { startMinutes: number; endMinutes: number }[]) => {
  const openingTimes: { [key in DayOfWeek]: WeeklyScheduleEvent[] } = {
    friday: [],
    monday: [],
    saturday: [],
    sunday: [],
    tuesday: [],
    thursday: [],
    wednesday: [],
  };

  const days: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  for (let i = 0; i < days.length; i += 1) {
    const sameDayPairs: WeeklyScheduleEvent[] = [];
    const overflowingPairs: WeeklyScheduleEvent[] = [];

    timePairs.forEach(pair => {
      if (pair.startMinutes < 24 * 60 && pair.endMinutes > 24 * 60) {
        sameDayPairs.push({
          type: 'open',
          value: {
            $date: pair.startMinutes * MINUTE_DURATION_MS,
          },
        });
        overflowingPairs.push({
          type: 'close',
          value: {
            $date: (pair.endMinutes - 24 * 60) * MINUTE_DURATION_MS,
          },
        });
      } else {
        sameDayPairs.push({
          type: 'open',
          value: {
            $date: pair.startMinutes * MINUTE_DURATION_MS,
          },
        });
        sameDayPairs.push({
          type: 'close',
          value: {
            $date: pair.endMinutes * MINUTE_DURATION_MS,
          },
        });
      }
    });
    openingTimes[days[i]].push(...sameDayPairs);

    if (overflowingPairs.length) {
      const nextDayIndex = days[i + 1] ? i + 1 : 0;

      openingTimes[days[nextDayIndex]].push(...overflowingPairs);
    }
  }

  const sort = (a: WeeklyScheduleEvent, b: WeeklyScheduleEvent) => a.value.$date - b.value.$date;

  const sortedOpeningTimes = {
    monday: R.sort(sort, openingTimes.monday),
    tuesday: R.sort(sort, openingTimes.tuesday),
    wednesday: R.sort(sort, openingTimes.wednesday),
    thursday: R.sort(sort, openingTimes.thursday),
    friday: R.sort(sort, openingTimes.friday),
    saturday: R.sort(sort, openingTimes.saturday),
    sunday: R.sort(sort, openingTimes.sunday),
  };

  return sortedOpeningTimes;
};

const findPeriodStartValue = (
  startDayRegularSchedule: WeeklyScheduleEvent[],
  startDayExceptionSchedule: WeeklyScheduleEvent[],
  periodStart: DateTime,
) => {
  const isRegularScheduleFirstEventClose =
    startDayRegularSchedule.length && startDayRegularSchedule[0].type === 'close';

  const firstRegularCloseEvent =
    isRegularScheduleFirstEventClose && startDayRegularSchedule[0].value.$date !== 24 * HOUR_DURATION_MS
      ? periodStart.plus({ millisecond: startDayRegularSchedule[0].value.$date })
      : periodStart;

  const isExceptionalScheduleFirstEventClose =
    startDayExceptionSchedule.length && startDayExceptionSchedule[0].type === 'close';

  const firstExceptionalCloseEvent =
    isExceptionalScheduleFirstEventClose && startDayExceptionSchedule[0].value.$date !== 24 * HOUR_DURATION_MS
      ? periodStart.plus({ millisecond: startDayExceptionSchedule[0].value.$date })
      : periodStart;

  const firstCloseEvent =
    firstRegularCloseEvent.valueOf() > firstExceptionalCloseEvent.valueOf()
      ? firstRegularCloseEvent
      : firstExceptionalCloseEvent;

  const firstExceptionalOpenEvent: DateTime | undefined = startDayExceptionSchedule.find(event => event.type === 'open')
    ? periodStart.plus({ milliseconds: startDayExceptionSchedule.find(event => event.type === 'open')?.value.$date })
    : undefined;

  return firstExceptionalOpenEvent && firstExceptionalOpenEvent.valueOf() < firstCloseEvent.valueOf()
    ? firstExceptionalOpenEvent
    : firstCloseEvent;
};

const findNextCloseEvent = (startDateTime: DateTime, venueOpeningTimes: WeeklySchedule) => {
  let end;
  let hours = 24;
  let day = startDateTime.plus({ hours });

  while (!end) {
    const dayEvents = venueOpeningTimes[DAYS_OF_WEEK[day.weekday - 1]];
    const firstCloseEventForDay = dayEvents.find(event => event.type === 'close');
    if (firstCloseEventForDay) {
      end = startDateTime.plus({ hours }).plus({ milliseconds: firstCloseEventForDay.value.$date });
      break;
    } else {
      hours += 24;
      day = day.plus({ hours });
    }
    if (day.weekday === startDateTime.weekday) {
      break;
    }
  }

  return end || startDateTime.plus({ hours: 24 });
};

const findPeriodEndValue = (
  exceptionOpeningTimes: WeeklySchedule,
  venueOpeningTimes: WeeklySchedule,
  endDayOpeningTimes: WeeklyScheduleEvent[],
  endDayExceptionTimes: WeeklyScheduleEvent[],
  periodEnd: DateTime,
) => {
  const isScheduleLastEventOpen =
    endDayOpeningTimes.length && endDayOpeningTimes[endDayOpeningTimes.length - 1].type === 'open';

  const isExceptionLastEventOpen =
    endDayExceptionTimes.length && endDayExceptionTimes[endDayExceptionTimes.length - 1].type === 'open';

  const lastVenueCloseEvent =
    (isScheduleLastEventOpen &&
      // finds the next day of week, because Luxon's DateTime#weekday is 1-7
      venueOpeningTimes[DAYS_OF_WEEK[periodEnd.weekday % DAYS_OF_WEEK.length]]
        .filter(event => event.type === 'close')
        .filter(event => event.value.$date < 24 * HOUR_DURATION_MS)
        .map(event => event.value.$date)
        .sort((a, b) => a - b)
        .map(value => periodEnd.plus({ hours: 24, milliseconds: value }))[0]) ||
    periodEnd.plus({ hours: 24 });

  const lastExceptionCloseEvent = isExceptionLastEventOpen
    ? findNextCloseEvent(periodEnd, exceptionOpeningTimes)
    : periodEnd.plus({ hours: 24 });

  return lastVenueCloseEvent.valueOf() > lastExceptionCloseEvent.valueOf()
    ? lastVenueCloseEvent
    : lastExceptionCloseEvent;
};

const checkForDST = (start: DateTime, current: DateTime) => {
  if (!start.isInDST && current.isInDST) {
    return current.minus({ hours: 1 });
  }

  if (start.isInDST && !current.isInDST) {
    return current.plus({ hours: 1 });
  }

  return current;
};

const findPeriodStartAndEndValues = (
  venueOpeningTimes: WeeklySchedule,
  exceptionOpeningTimes: WeeklySchedule,
  periodStart: DateTime,
  periodEnd: DateTime,
) => {
  const startDay = DAYS_OF_WEEK[periodStart.weekday - 1];
  const endDay = DAYS_OF_WEEK[periodEnd.weekday - 1];

  const startDayOpeningTimes = venueOpeningTimes[startDay];
  const endDayOpeningTimes = venueOpeningTimes[endDay];

  const startDayExceptionTimes = exceptionOpeningTimes[startDay];
  const endDayExceptionTimes = exceptionOpeningTimes[endDay];

  const start = findPeriodStartValue(startDayOpeningTimes, startDayExceptionTimes, periodStart);

  const end = findPeriodEndValue(
    exceptionOpeningTimes,
    venueOpeningTimes,
    endDayOpeningTimes,
    endDayExceptionTimes,
    periodEnd,
  );

  return {
    start: {
      $date: checkForDST(periodStart, start).valueOf(),
    },
    end: {
      $date: checkForDST(periodEnd, end).valueOf(),
    },
  };
};

export const periodToException = (
  entry: SpecialHoursInputEntry,
  period: DatePeriod,
  venueOpeningTimes: WeeklySchedule,
) => {
  const exceptionOpeningTimes = getExceptionOpeningTimes(
    extractTimePairs(entry.closedAllDay, entry.open24Hours, entry.timePairs),
  );

  const exceptionPeriod = findPeriodStartAndEndValues(
    venueOpeningTimes,
    exceptionOpeningTimes,
    period.start,
    period.end,
  );

  return {
    message: entry.message.trim(),
    opening_times: {
      ...exceptionOpeningTimes,
    },
    period: exceptionPeriod,
  };
};

const adjustOverlappingPeriods = (exceptions: SpecialHoursEntryAttrs[], timezone: string): SpecialHoursEntryAttrs[] => {
  const sortedExceptions = R.sort((a, b) => a.period.start.$date - b.period.start.$date, exceptions);

  let adjustedExceptions = R.clone(sortedExceptions);
  for (let i = 1; i < sortedExceptions.length; i += 1) {
    const prevException = adjustedExceptions[i - 1];
    const currException = adjustedExceptions[i];

    if (prevException.period.end.$date > currException.period.start.$date) {
      const prevExceptionOpeningTimes =
        prevException.opening_times[
          DAYS_OF_WEEK[DateTime.fromMillis(prevException.period.end.$date).setZone(timezone).weekday - 1]
        ];

      const currExceptionOpeningTimes =
        currException.opening_times[
          DAYS_OF_WEEK[DateTime.fromMillis(currException.period.start.$date).setZone(timezone).weekday - 1]
        ];

      const lastClosingTimeForPreviousException = prevExceptionOpeningTimes
        .filter(event => event.type === 'close')
        .map(event =>
          DateTime.fromMillis(prevException.period.end.$date)
            .setZone(timezone)
            .startOf('day')
            .set({
              hour: Math.floor(event.value.$date / HOUR_DURATION_MS),
              minute: Math.floor((event.value.$date % HOUR_DURATION_MS) / MINUTE_DURATION_MS),
              second: Math.floor((event.value.$date % MINUTE_DURATION_MS) / SECOND_DURATION_MS),
              millisecond: event.value.$date % SECOND_DURATION_MS,
            })
            .valueOf(),
        )
        .find(closingTime => closingTime <= prevException.period.end.$date);

      const firstOpeningTimeForCurrentException = currExceptionOpeningTimes
        .filter(event => event.type === 'open')
        .map(event =>
          DateTime.fromMillis(currException.period.start.$date)
            .setZone(timezone)
            .startOf('day')
            .set({
              hour: Math.floor(event.value.$date / HOUR_DURATION_MS),
              minute: Math.floor((event.value.$date % HOUR_DURATION_MS) / MINUTE_DURATION_MS),
              second: Math.floor((event.value.$date % MINUTE_DURATION_MS) / SECOND_DURATION_MS),
              millisecond: event.value.$date % SECOND_DURATION_MS,
            })
            .valueOf(),
        )
        .find(openingTime => openingTime >= currException.period.start.$date);

      const hoursOverlap =
        lastClosingTimeForPreviousException !== undefined &&
        firstOpeningTimeForCurrentException !== undefined &&
        lastClosingTimeForPreviousException > firstOpeningTimeForCurrentException;

      // if hours overlap, don't do any period adjustments
      // prevents merging of overlapping hours
      if (hoursOverlap) {
        break;
      }

      const usePrevHourDate = prevException.period.end.$date === lastClosingTimeForPreviousException;

      const newPrevEndDate = usePrevHourDate ? prevException.period.end.$date : currException.period.start.$date;
      const newCurrStartDate = newPrevEndDate;

      const adjustedPrevException = {
        ...prevException,
        period: {
          ...prevException.period,
          end: {
            $date: newPrevEndDate,
          },
        },
      };

      const adjustedCurrException = {
        ...currException,
        period: {
          ...currException.period,
          start: {
            $date: newCurrStartDate,
          },
        },
      };
      adjustedExceptions = R.update(i - 1, adjustedPrevException, adjustedExceptions);
      adjustedExceptions = R.update(i, adjustedCurrException, adjustedExceptions);
    }
  }

  return adjustedExceptions;
};

const fromSpecialHoursInputEntriesToSpecialHours = (
  entries: SpecialHoursInputEntry[],
  timezone: string,
  venueOpeningTimes: WeeklySchedule | null,
): SpecialHoursEntryAttrs[] => {
  if (entries.length === 1 && entries[0].periods.length === 0) {
    return [];
  }

  const openingTimes = venueOpeningTimes || {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };

  const exceptions = entries
    .map(entry => {
      return entry.periods.map(period => {
        return periodToException(entry, period, openingTimes);
      });
    })
    .flat();

  return adjustOverlappingPeriods(exceptions, timezone);
};

export default fromSpecialHoursInputEntriesToSpecialHours;
